import type { ToolsListFilterParams } from '@/api/tools.api';
import { toolsApi } from '@/api/tools.api';
import { useToolSystem } from '@/context/ToolSystemContext';
import type { CreateTool, ToolModel, UpdateTool } from '@/types/tool.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const DEFAULT_PAGE_SIZE = 10;

function filterFromParams(params: URLSearchParams): ToolsListFilterParams {
  const tool_types = params.has('tool_types') ? params.getAll('tool_types') : null;
  const order = (params.get('order') as 'asc' | 'desc') ?? 'asc';
  return { tool_types, order };
}

function applyFilterToParams(params: URLSearchParams, filter: ToolsListFilterParams): void {
  // Left over in bookmarked URLs from before holder and tool number moved to the assembly.
  params.delete('holder_ids');
  params.delete('tool_number');
  params.delete('tool_types');
  if (filter.tool_types != null) {
    filter.tool_types.forEach((tt) => params.append('tool_types', tt));
  }
  params.delete('order');
  if (filter.order && filter.order !== 'asc') {
    params.set('order', filter.order);
  }
}

export const useTools = () => {
  const { toolSystem } = useToolSystem();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tools, setTools] = useState<ToolModel[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const listFilter = useMemo(
    // biome-ignore lint/style/useNamingConvention: Backend API uses snake_case
    () => ({ ...filterFromParams(searchParams), tool_system: toolSystem }),
    [searchParams, toolSystem]
  );
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize = Number(searchParams.get('size') ?? DEFAULT_PAGE_SIZE);

  const setPage = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('page', String(next));
        return p;
      });
    },
    [setSearchParams]
  );

  const setPageSize = useCallback(
    (next: number) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        p.set('size', String(next));
        p.set('page', '1');
        return p;
      });
    },
    [setSearchParams]
  );

  const setListFilter = useCallback(
    (next: ToolsListFilterParams | ((prev: ToolsListFilterParams) => ToolsListFilterParams)) => {
      setSearchParams((prev) => {
        const p = new URLSearchParams(prev);
        const resolved = typeof next === 'function' ? next(filterFromParams(prev)) : next;
        applyFilterToParams(p, resolved);
        p.set('page', '1');
        return p;
      });
    },
    [setSearchParams]
  );

  const fetchTools = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [data, total] = await Promise.all([
        toolsApi.getAll({ ...listFilter, page, size: pageSize }),
        toolsApi.getCount(listFilter),
      ]);
      setTools(data);
      setTotalCount(total);
    } catch (_err) {
      const message = 'Failed to load tools';
      setError(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  }, [listFilter, page, pageSize]);

  useEffect(() => {
    fetchTools();
  }, [fetchTools]);

  const updateTool = useCallback(async (toolId: string, body: UpdateTool): Promise<ToolModel | null> => {
    try {
      const updated = await toolsApi.update(toolId, body);
      setTools((prev) => prev.map((t) => (t.id === toolId ? updated : t)));
      return updated;
    } catch (err) {
      throw err;
    }
  }, []);

  const createTool = useCallback(
    async (body: CreateTool): Promise<void> => {
      await toolsApi.create(body);
      await fetchTools();
    },
    [fetchTools]
  );

  const deleteTool = useCallback(async (toolId: string): Promise<void> => {
    await toolsApi.delete(toolId);
    setTools((prev) => prev.filter((t) => t.id !== toolId));
    setTotalCount((prev) => (prev != null ? prev - 1 : prev));
  }, []);

  const hasNextPage = pageSize > 0 && tools.length === pageSize;
  const hasPrevPage = page > 1;
  const totalPages = totalCount != null && pageSize > 0 ? Math.ceil(totalCount / pageSize) : null;

  return {
    tools,
    isLoading,
    error,
    refetch: fetchTools,
    createTool,
    updateTool,
    deleteTool,
    page,
    setPage,
    pageSize,
    setPageSize,
    hasNextPage,
    hasPrevPage,
    totalPages,
    totalCount,
    listFilter,
    setListFilter,
  };
};
